/**
 * NEC 2023 Table 430.52(C)(1)
 * Maximum Rating or Setting of Motor Branch-Circuit Short-Circuit
 * and Ground-Fault Protective Devices
 *
 * Percent of full-load current by motor type and device. The table has two
 * squirrel-cage rows for the instantaneous-trip column: "Design B
 * energy-efficient and Design B premium efficiency" at 1100%, and every
 * other squirrel-cage motor at 800%. Since EISA every general-purpose motor
 * sold is premium efficiency, so the 1100% row is the one that ordinarily
 * applies. 430.52(C)(3) Exception No. 1 lets the instantaneous trip go to
 * 1300% (1700% for the energy-efficient / premium row) where the motor will
 * not otherwise start.
 */

import type { NECReference } from '../types';

/**
 * Motor design types per NEMA MG-1
 * - B: General purpose, standard efficiency (pre-EISA)
 * - B-EFF: Design B energy-efficient / premium efficiency (NEMA Premium;
 *   higher inrush, 1100% instantaneous-trip row)
 * - C: High starting torque
 * - D: High slip, high starting torque
 * - E: High efficiency (legacy; the 2023 table no longer lists it — kept so
 *   saved data that names it still resolves to the 1100% row)
 */
export type MotorDesign = 'A' | 'B' | 'B-EFF' | 'C' | 'D' | 'E';

/** How Table 430.52(C)(1) names each design, for every place the app shows one. */
export const MOTOR_DESIGN_LABELS: Record<MotorDesign, string> = {
  A: 'Design A',
  B: 'Design B (standard efficiency)',
  'B-EFF': 'Design B energy-efficient / premium efficiency',
  C: 'Design C',
  D: 'Design D',
  E: 'Design E (legacy)',
};

/** True for the designs on the 1100% instantaneous-trip row. */
export function isEnergyEfficientDesign(design: MotorDesign): boolean {
  return design === 'B-EFF' || design === 'E';
}

/**
 * Protection device types for motor circuits
 */
export type ProtectionDeviceType =
  | 'instantaneous-trip'      // Motor Circuit Protector (MCP)
  | 'inverse-time'            // Thermal-magnetic circuit breaker
  | 'dual-element-fuse'       // Time-delay fuse (Class RK1, RK5, J, etc.)
  | 'non-time-delay-fuse';    // Fast-acting fuse (Class K, H, etc.)

/**
 * MCP limits as percentage of motor FLA
 */
export interface MCPLimits {
  design: MotorDesign;
  instantaneousTripMax: number;   // % of FLA for MCP
  inverseTimeMax: number;         // % of FLA for thermal-mag breaker
  dualElementFuseMax: number;     // % of FLA for time-delay fuse
  nonTimeDelayFuseMax: number;    // % of FLA for non-time-delay fuse
}

/**
 * NEC Table 430.52 - Maximum Rating or Setting of Motor Branch-Circuit
 * Short-Circuit and Ground-Fault Protective Devices
 *
 * Values are percentages of motor full-load current (FLA)
 *
 * Per NEC 430.52(C)(1):
 * - (a) If the values for the motor FLA do not correspond to standard
 *   sizes/settings, the next higher standard size may be used per 240.6(A)
 * - (b) Where that rating is not sufficient for starting, an inverse-time
 *   breaker may go to 400% (≤ 100 A) / 300%, fuses per 430.52(C)(1)(b)
 * - 430.52(C)(3) Exception No. 1: an instantaneous trip may go to 1300%
 *   (1700% for Design B energy-efficient / premium efficiency)
 */
export const NEC_TABLE_430_52: MCPLimits[] = [
  // Design A - High starting torque, high starting current
  // Rarely used, limited applications
  {
    design: 'A',
    instantaneousTripMax: 800,
    inverseTimeMax: 250,
    dualElementFuseMax: 175,
    nonTimeDelayFuseMax: 300,
  },

  // Design B - standard efficiency (the "other than Design B energy-efficient /
  // premium efficiency" squirrel-cage row)
  {
    design: 'B',
    instantaneousTripMax: 800,
    inverseTimeMax: 250,
    dualElementFuseMax: 175,
    nonTimeDelayFuseMax: 300,
  },

  // Design B energy-efficient and Design B premium efficiency (NEMA Premium):
  // higher inrush, so the table gives the instantaneous trip 1100%
  {
    design: 'B-EFF',
    instantaneousTripMax: 1100,
    inverseTimeMax: 250,
    dualElementFuseMax: 175,
    nonTimeDelayFuseMax: 300,
  },

  // Design C - High starting torque
  // Used for hard-to-start loads (conveyors, crushers)
  {
    design: 'C',
    instantaneousTripMax: 800,
    inverseTimeMax: 250,
    dualElementFuseMax: 175,
    nonTimeDelayFuseMax: 300,
  },

  // Design D - High slip, high starting torque
  // Used for high-inertia loads (punch presses, hoists)
  {
    design: 'D',
    instantaneousTripMax: 800,
    inverseTimeMax: 250,
    dualElementFuseMax: 175,
    nonTimeDelayFuseMax: 300,
  },

  // Design E - legacy designation, not in the 2023 table; resolves to the
  // energy-efficient row so saved data that names it keeps working
  {
    design: 'E',
    instantaneousTripMax: 1100,
    inverseTimeMax: 250,
    dualElementFuseMax: 175,
    nonTimeDelayFuseMax: 300,
  },
];

/**
 * Standard breaker/fuse sizes per NEC 240.6(A)
 * Used to round up calculated MCP values to next standard size
 */
export const STANDARD_OVERCURRENT_SIZES = [
  15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100,
  110, 125, 150, 175, 200, 225, 250, 300, 350, 400, 450, 500,
  600, 700, 800, 1000, 1200, 1600, 2000, 2500, 3000, 4000, 5000, 6000,
];

/**
 * Get MCP limits for a specific motor design
 */
export function getMCPLimits(design: MotorDesign): MCPLimits | undefined {
  return NEC_TABLE_430_52.find((entry) => entry.design === design);
}

/**
 * Get maximum percentage for a specific protection device type
 */
export function getMCPMaxPercent(
  design: MotorDesign,
  deviceType: ProtectionDeviceType
): number | undefined {
  const limits = getMCPLimits(design);
  if (!limits) return undefined;

  switch (deviceType) {
    case 'instantaneous-trip':
      return limits.instantaneousTripMax;
    case 'inverse-time':
      return limits.inverseTimeMax;
    case 'dual-element-fuse':
      return limits.dualElementFuseMax;
    case 'non-time-delay-fuse':
      return limits.nonTimeDelayFuseMax;
    default:
      return undefined;
  }
}

/**
 * Round up to next standard overcurrent device size per NEC 240.6(A)
 */
export function roundToStandardSize(amps: number): number {
  for (const size of STANDARD_OVERCURRENT_SIZES) {
    if (size >= amps) {
      return size;
    }
  }
  // If larger than all standard sizes, return the calculated value
  return Math.ceil(amps);
}

/**
 * Check if a calculated MCP size exceeds the allowed maximum
 */
export function isMCPSizeAllowed(
  calculatedAmps: number,
  fla: number,
  design: MotorDesign,
  deviceType: ProtectionDeviceType
): boolean {
  const maxPercent = getMCPMaxPercent(design, deviceType);
  if (maxPercent === undefined) return false;

  const maxAmps = (fla * maxPercent) / 100;
  return calculatedAmps <= maxAmps;
}

/**
 * Get all valid motor designs
 */
export function getValidMotorDesigns(): MotorDesign[] {
  return ['A', 'B', 'B-EFF', 'C', 'D', 'E'];
}

/**
 * NEC reference for Table 430.52
 */
export const NEC_TABLE_430_52_REF: NECReference = {
  table: 'NEC 2023 Table 430.52(C)(1)',
  article: '430.52',
  description: 'Maximum Rating of Motor Branch-Circuit Short-Circuit and Ground-Fault Protective Devices',
};

/**
 * NEC reference for standard overcurrent device sizes
 */
export const NEC_SECTION_240_6_REF: NECReference = {
  table: 'NEC 2023 Section 240.6(A)',
  article: '240.6',
  description: 'Standard Ampere Ratings for Fuses and Inverse Time Circuit Breakers',
};
